import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import type { Projection } from '../../services/household-retirement-service';
import { money, monthYear } from './format';

const shiftLabel = (s: number) => (s === 0 ? 'Base' : s > 0 ? `Prime +${s}%` : `Prime ${s}%`);

/**
 * Debts under the prime-rate sensitivity (payoff date and total interest at
 * prime −1 / base / +1), and the household's available credit: reserves and
 * unused credit lines — liquidity, never assets.
 */
export const DebtsPanel: React.FC<{ projection: Projection; currency?: string }> = ({ projection, currency = 'CAD' }) => {
  const m = (v: number) => money(v, currency);
  const sens = projection.debt_sensitivity ?? [];
  const ac = projection.available_credit;
  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="stretch">
      <Card sx={{ flex: 2 }} data-testid="debt-sensitivity">
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Debts — prime {projection.prime_pct}% (flat){projection.prime_shift_pct ? `, scenario ${shiftLabel(projection.prime_shift_pct)}` : ''}
          </Typography>
          {(projection.debts ?? []).filter((d) => d.balance > 0).map((d) => (
            <Typography key={d.account_id ?? d.name} variant="body2" sx={{ mt: 0.5 }}>
              <b>{d.label || d.name}</b>{' — '}
              {d.rate_type === 'variable' && d.spread_pct != null
                ? `variable, prime ${d.spread_pct >= 0 ? '+' : '−'} ${Math.abs(d.spread_pct)} = ${d.rate_pct}%`
                : `${d.rate_pct}% fixed`}
              {`; ${d.payment_type ?? 'fixed'} payment ${m(d.monthly_payment)}/mo`}
              {d.term_end && (d.kind === 'mortgage' || d.kind === 'heloc')
                ? `; term to ${monthYear(d.term_end)}, renews at ${d.renewal === 'same_spread' ? 'the same spread' : 'the same rate'} (assumed)`
                : d.term_end ? `; term to ${monthYear(d.term_end)}` : ''}
              {d.amortization_months ? `; ~${(d.amortization_months / 12).toFixed(1)} yrs left` : ''}
              {d.term_note && (
                <Typography component="div" variant="caption" color="warning.main">{d.term_note}</Typography>
              )}
              {d.payment_history && (
                <Typography component="span" variant="caption" color="text.secondary">{` (${d.payment_history})`}</Typography>
              )}
            </Typography>
          ))}
          <Table size="small" sx={{ mt: 1 }} aria-label="Prime sensitivity">
            <TableHead>
              <TableRow>
                <TableCell>Debt</TableCell>
                <TableCell>Prime path</TableCell>
                <TableCell align="right">Rate</TableCell>
                <TableCell align="right">Payment</TableCell>
                <TableCell>Paid off</TableCell>
                <TableCell align="right">Total interest</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sens.map((s, i) => (
                <TableRow key={i} selected={s.prime_shift_pct === (projection.prime_shift_pct ?? 0) && sens.filter((x) => x.debt === s.debt).length > 1}>
                  <TableCell>{s.debt}</TableCell>
                  <TableCell>{shiftLabel(s.prime_shift_pct)}</TableCell>
                  <TableCell align="right" className="fw-num">{s.rate_pct.toFixed(2)}%</TableCell>
                  <TableCell align="right" className="fw-num">{m(s.payment)}</TableCell>
                  <TableCell>{s.never_pays_off ? 'never' : monthYear(s.payoff_date)}</TableCell>
                  <TableCell align="right" className="fw-num">{m(s.total_interest)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card sx={{ flex: 1 }} data-testid="available-credit">
        <CardContent>
          <Typography variant="overline" color="text.secondary">Available credit / emergency liquidity</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>Cash reserves: <b className="fw-num">{m(ac.reserves)}</b></Typography>
          {ac.credit_lines.map((c) => (
            <Box key={c.name} sx={{ mt: 1 }} data-testid={`credit-line-${c.name}`}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2"><b>{c.name}{c.readvanceable ? ' (readvanceable)' : ''}</b></Typography>
                <Chip size="small" variant="outlined" label={c.status} />
              </Stack>
              <Typography variant="body2">{c.summary}</Typography>
              {c.readvanceable ? (
                <>
                  {c.basis_note && <Typography variant="caption" color="text.secondary" component="div">Basis: {c.basis_note}</Typography>}
                  {(c.scenarios ?? []).length > 1 && (
                    <Typography variant="caption" color="text.secondary" component="div">
                      In 5 years: {(c.scenarios ?? []).map((s) => `${s.prime_shift_pct === 0 ? 'base' : `prime ${s.prime_shift_pct > 0 ? '+' : ''}${s.prime_shift_pct}%`} ${m(s.available_in_5y)}`).join(' · ')}
                    </Typography>
                  )}
                </>
              ) : c.limit == null && (
                <Typography variant="caption" color="warning.main" component="div">{c.limit_prompt ?? 'limit: enter amount'}</Typography>
              )}
            </Box>
          ))}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>{ac.note}</Typography>
        </CardContent>
      </Card>
    </Stack>
  );
};
